import React from 'react';
import { DossierTransfert, Fichier } from '../utils/types';
import { formatDate, formatFileSize } from '../utils/helpers';
import { canDeleteSubfoldersAndFiles } from '../utils/permissions';

interface TransfertModalProps {
  isOpen: boolean;
  transfert: DossierTransfert;
  currentUserRole: string;
  onClose: () => void;
  onDownloadFile: (fileId: string, nomOriginal: string) => void;
  onDeleteFile: (fileId: string, nomOriginal: string, chemin: string) => void;
  onDeleteTransfert: (transfert: DossierTransfert) => void;
}

const TransfertModal: React.FC<TransfertModalProps> = ({
  isOpen,
  transfert,
  currentUserRole,
  onClose,
  onDownloadFile,
  onDeleteFile,
  onDeleteTransfert
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4 backdrop-blur-sm">
      <div className="bg-white rounded-xl sm:rounded-3xl max-w-full lg:max-w-4xl w-full max-h-[90vh] overflow-hidden shadow-2xl border-2 sm:border-4 border-blue-200 mx-2 sm:mx-4">
        <ModalHeader transfert={transfert} onClose={onClose} />
        <ModalContent
          transfert={transfert}
          currentUserRole={currentUserRole}
          onDownloadFile={onDownloadFile}
          onDeleteFile={onDeleteFile}
          onDeleteTransfert={onDeleteTransfert}
          onClose={onClose}
        />
      </div>
    </div>
  );
};

const ModalHeader: React.FC<{ transfert: DossierTransfert; onClose: () => void }> = ({ transfert, onClose }) => (
  <div className="flex items-center justify-between p-4 sm:p-6 border-b-2 sm:border-b-4 border-purple-300 bg-gradient-to-r from-blue-600 to-blue-700">
    <div className="flex-1 min-w-0">
      <h3 className="text-lg sm:text-xl font-semibold text-white truncate">Dossier Transfert: {transfert.reference}</h3>
      <p className="text-purple-100 text-xs sm:text-sm mt-1">Créé le {formatDate(transfert.dateCreation)}</p>
    </div>
    <button
      onClick={onClose}
      className="text-white hover:text-gray-200 p-2 hover:bg-white/20 rounded-full transition-all duration-300 flex-shrink-0 ml-2"
    >
      <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
      </svg>
    </button>
  </div>
);

const ModalContent: React.FC<{
  transfert: DossierTransfert;
  currentUserRole: string;
  onDownloadFile: (fileId: string, nomOriginal: string) => void;
  onDeleteFile: (fileId: string, nomOriginal: string, chemin: string) => void;
  onDeleteTransfert: (transfert: DossierTransfert) => void;
  onClose: () => void;
}> = ({ transfert, currentUserRole, onDownloadFile, onDeleteFile, onDeleteTransfert, onClose }) => (
  <div className="p-4 sm:p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
    <InfoSections transfert={transfert} />
    <FilesSection
      transfert={transfert}
      currentUserRole={currentUserRole}
      onDownloadFile={onDownloadFile}
      onDeleteFile={onDeleteFile}
    />
    <ActionsSection
      transfert={transfert}
      currentUserRole={currentUserRole}
      onClose={onClose}
      onDeleteTransfert={onDeleteTransfert}
    />
  </div>
);

const InfoSections: React.FC<{ transfert: DossierTransfert }> = ({ transfert }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
    <InfoSection
      title="Informations client"
      icon="user"
      color="blue"
      content={
        <>
          <InfoItem label="Nom complet" value={`${transfert.nom} ${transfert.prenom}`} />
          <InfoItem label="Email" value={transfert.email} />
          <InfoItem label="Téléphone" value={transfert.telephone || 'Non renseigné'} />
          <InfoItem label="Profession" value={transfert.profession} />
          <InfoItem label="Entreprise" value={transfert.entreprise || 'Pas défini'} />
          <InfoItem label="Sexe" value={transfert.sexe === 'H' ? 'Homme' : 'Femme'} />
        </>
      }
    />
    <InfoSection
      title="Détails du transfert"
      icon="transfer"
      color="purple"
      content={
        <>
          <InfoItem label="Type de transfert" value={transfert.typeTransfert} />
          <InfoItem label="Date de dépôt" value={formatDate(transfert.dateCreation)} />
          <InfoItem 
            label="Statut" 
            value={
              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                transfert.statut === 'en_attente' ? 'bg-yellow-100 text-yellow-800' :
                transfert.statut === 'partiellement_apuré' ? 'bg-orange-100 text-orange-800' :
                transfert.statut === 'apuré' ? 'bg-green-100 text-green-800' :
                transfert.statut === 'archivé' ? 'bg-blue-100 text-blue-800' :
                'bg-red-100 text-red-800'
              }`}>
                {transfert.statut}
              </span>
            } 
          />
          {transfert.motifRejet && (
            <InfoItem label="Motif rejet" value={<span className="text-red-600">{transfert.motifRejet}</span>} />
          )}
        </>
      }
    />
  </div>
);

const InfoSection: React.FC<{
  title: string;
  icon: string;
  color: 'blue' | 'purple';
  content: React.ReactNode;
}> = ({ title, icon, color, content }) => {
  const colorClasses = {
    blue: { bg: 'bg-blue-50', icon: 'text-blue-500' },
    purple: { bg: 'bg-purple-50', icon: 'text-blue-500' }
  };

  const getIcon = (iconName: string) => {
    switch (iconName) {
      case 'user':
        return <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />;
      case 'transfer':
        return <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />;
      default:
        return null;
    }
  };

  return (
    <div className={`${colorClasses[color].bg} p-3 sm:p-4 rounded-lg`}>
      <h4 className="font-medium text-gray-900 mb-2 sm:mb-3 flex items-center">
        <svg className={`w-4 h-4 sm:w-5 sm:h-5 mr-2 ${colorClasses[color].icon}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          {getIcon(icon)}
        </svg>
        {title}
      </h4>
      <div className="space-y-1 sm:space-y-2 text-xs sm:text-sm">
        {content}
      </div>
    </div>
  );
};

const InfoItem: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div>
    <span className="font-medium text-gray-500">{label}:</span>
    <div className="text-gray-900">{value}</div>
  </div>
);

const FilesSection: React.FC<{
  transfert: DossierTransfert;
  currentUserRole: string;
  onDownloadFile: (fileId: string, nomOriginal: string) => void;
  onDeleteFile: (fileId: string, nomOriginal: string, chemin: string) => void;
}> = ({ transfert, currentUserRole, onDownloadFile, onDeleteFile }) => (
  <div className="bg-white border border-blue-200 rounded-xl p-4 sm:p-6">
    <h4 className="text-base sm:text-lg font-medium text-gray-900 mb-3 sm:mb-4 flex items-center">
      <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-2 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
      Fichiers ({transfert.fichiers.length})
    </h4>

    {transfert.fichiers.length > 0 ? (
      <div className="space-y-2 sm:space-y-3">
        {transfert.fichiers.map((fichier, index) => (
          <FileItem
            key={index}
            fichier={fichier}
            currentUserRole={currentUserRole}
            onDownloadFile={onDownloadFile}
            onDeleteFile={onDeleteFile}
          />
        ))}
      </div>
    ) : (
      <div className="text-center py-6 sm:py-8 text-gray-500">
        <svg className="w-10 h-10 sm:w-12 sm:h-12 mx-auto text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        <p className="mt-2 text-sm">Aucun fichier dans ce dossier</p>
      </div>
    )}
  </div>
);

const FileItem: React.FC<{
  fichier: Fichier;
  currentUserRole: string;
  onDownloadFile: (fileId: string, nomOriginal: string) => void;
  onDeleteFile: (fileId: string, nomOriginal: string, chemin: string) => void;
}> = ({ fichier, currentUserRole, onDownloadFile, onDeleteFile }) => (
  <div className="flex items-center justify-between p-3 sm:p-4 border border-blue-100 rounded-lg bg-purple-50/50">
    <div className="flex items-center space-x-2 sm:space-x-3 min-w-0 flex-1">
      <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
        <svg className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      </div>

      <div className="min-w-0 flex-1">
        <p className="font-medium text-gray-900 truncate text-sm sm:text-base">
          {fichier.nomOriginal || fichier.nom}
        </p>
        <p className="text-xs text-gray-600">{formatFileSize(fichier.taille)} • {fichier.extension}</p>
      </div>
    </div>
    <div className="flex items-center space-x-1 sm:space-x-2 flex-shrink-0 ml-2">
      <button
        onClick={() => onDownloadFile(fichier._id, fichier.nomOriginal || fichier.nom)}
        className="text-blue-600 hover:text-purple-800 bg-blue-100 hover:bg-purple-200 px-2 sm:px-3 py-1 rounded transition-all duration-300 text-xs sm:text-sm"
      >
        Télécharger
      </button>
      {canDeleteSubfoldersAndFiles(currentUserRole) && (
        <button
          onClick={() => onDeleteFile(fichier._id, fichier.nomOriginal || fichier.nom, fichier.chemin)}
          className="text-red-600 hover:text-red-800 bg-red-50 hover:bg-red-100 px-2 sm:px-3 py-1 rounded transition-all duration-300 text-xs sm:text-sm"
        >
          Supprimer
        </button>
      )}
    </div>
  </div>
);

const ActionsSection: React.FC<{
  transfert: DossierTransfert;
  currentUserRole: string;
  onClose: () => void;
  onDeleteTransfert: (transfert: DossierTransfert) => void;
}> = ({ transfert, currentUserRole, onClose, onDeleteTransfert }) => (
  <div className="mt-4 sm:mt-6 pt-4 sm:pt-6 border-t flex justify-end space-x-2 sm:space-x-3">
    <button
      onClick={onClose}
      className="px-3 sm:px-4 py-2 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-all duration-300 text-sm"
    >
      Fermer
    </button>
    {(currentUserRole === 'admin_bank' || currentUserRole === 'superviseur') && transfert.statut !== 'rejeté' && (
      <button
        onClick={() => onDeleteTransfert(transfert)}
        className="px-3 sm:px-4 py-2 text-white bg-red-600 hover:bg-red-700 rounded-lg transition-all duration-300 text-sm"
      >
        Supprimer le dossier
      </button>
    )}
  </div>
);

export default TransfertModal;
